import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PlaidLink, PlaidLinkOnSuccessMetadata } from "react-plaid-link";
import GoBack from "../components/GoBack";
import {
  getMyIdentityVerification,
  createIdentityLinkToken,
  postIdentityVerificationCallback,
  IdentityVerificationSummary,
} from "../api/services";
import { useTranslation } from "react-i18next";

const IdentityVerification: React.FC = () => {
  const { t } = useTranslation();
  const { id: applicationId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [identity, setIdentity] = useState<IdentityVerificationSummary | null>(
    null,
  );
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    (async () => {
      try {
        const res = await getMyIdentityVerification();
        if (cancelled) return;
        setIdentity(res.data ?? null);

        if (res.data?.status !== "completed") {
          const tokenRes = await createIdentityLinkToken();
          if (!cancelled) setLinkToken(tokenRes.data.link_token);
        }
      } catch (err) {
        console.error(err);
        if (!cancelled) {
          setError(t("identityVerification.errorFetchingLinkToken"));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const handleSuccess = async (
    _publicToken: string,
    metadata: PlaidLinkOnSuccessMetadata,
  ) => {
    setLoading(true);

    const linkSessionId = metadata.link_session_id;
    if (!linkSessionId) {
      console.error("No link_session_id in metadata:", metadata);
      setError(t("identityVerification.errorNoSessionId"));
      setLoading(false);
      return;
    }

    try {
      await postIdentityVerificationCallback(linkSessionId);
      setIdentity({ id: 0, status: "completed" });
      if (applicationId) {
        navigate(`/application/${applicationId}`, { replace: true });
      }
    } catch (err) {
      console.error(err);
      setError(t("identityVerification.errorCompletingVerification"));
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="mt-6">
        <GoBack />
        <p>{t("identityVerification.loading", "Loading...")}</p>
      </div>
    );
  }

  if (identity?.status === "completed") {
    return (
      <div className="mt-6">
        <GoBack />
        <h2>{t("identityVerification.title", "Identity Verification")}</h2>
        <div className="mb-6 rounded-xl bg-white shadow-sm">
          <div className="py-10 text-center p-5">
            <div className="mb-4 text-6xl text-success">✔︎</div>
            <h5 className="font-semibold">
              {t(
                "identityVerification.verifiedMessage",
                "Your identity has been verified.",
              )}
            </h5>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6">
      <GoBack />
      <h2>{t("identityVerification.title", "Identity Verification")}</h2>
      <div className="mb-6 rounded-xl bg-white shadow-sm">
        <div className="py-6 text-center p-5">
          <p>
            <img alt="Plaid" src="/assets/plaid.svg" />
          </p>
          <h5 className="font-semibold">{t("identityVerification.intro1")}</h5>
          <p>{t("identityVerification.intro2")}</p>
          <p>
            <strong>{t("identityVerification.intro3")}</strong>
          </p>

          {error && (
            <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
              {error}
            </div>
          )}

          {linkToken ? (
            <PlaidLink
              token={linkToken}
              product={["identity_verification"]}
              clientName="Lighthouse"
              env="production"
              onSuccess={handleSuccess}
              onExit={(err, metadata) => {
                console.error("Plaid Identity exit", err, metadata);
              }}
              className="rounded-lg bg-accent px-5 py-2.5 text-sm font-medium text-black border-0 transition-colors hover:bg-accent-hover"
            >
              {t("identityVerification.startButton", "Start Verification")}
            </PlaidLink>
          ) : (
            <div className="rounded-lg bg-amber-50 px-4 py-3 text-sm text-warning">
              {t(
                "identityVerification.noLinkToken",
                "Unable to initialize verification.",
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default IdentityVerification;
